import * as z from "zod";
import { userFacingError } from "../authorization";
import {
  CA_CERTIFICATE_FIELDS,
  certificateAuditMetadata,
  trimmedCaCertificate,
} from "../connectors/caCertificate";
import { applyConfigurationPatch } from "../connectors/configurationPatch";
import { dcrRegistrationChanged, withoutDiscovery } from "../connectors/dcr";
import { canAuthorizeVisitors } from "../connectors/isDraft";
import { MANAGED_AI_CONNECTOR_TYPE } from "../connectors/managedAi";
import { connectorDestinationAuditMetadata } from "../connectors/auditDestination";
import { normalizedConnectorIdentity } from "../connectors/normalizeUrlPattern";
import {
  presentedCertificateStatus,
  withoutPresentedCertificate,
  withoutPresentedCertificateTrust,
} from "../connectors/presentedCertificate";
import { stripNullBytesDeep } from "../stripNullBytes";
import { mutator } from "./mutator";
import { disconnectOAuthConnection } from "./resetOAuthConnection";
import { stringRecord } from "./utils";

export const CONNECTOR_NOTE_MAX_LENGTH = 280;

export const updateConnectorSchema = z.object({
  connectedAppId: z.string(),
  name: z.string().optional(),
  identifier: z.string().optional(),
  urlPattern: z.string().optional(),
  configuration: stringRecord.optional(),
  configurationPatch: z
    .record(z.string(), z.string().nullable())
    .transform(stripNullBytesDeep)
    .optional(),
  secrets: stringRecord.optional(),
  destinationProofs: stringRecord.optional(),
  oauthConnectionId: z.string().optional(),
  note: z.string().optional(),
});

export const updateConnectedApp = mutator({
  redactKeys: ["secrets", "destinationProofs"],
  schema: updateConnectorSchema,
  resolve: {
    app: { kind: "connectedApp", from: "connectedAppId" },
  },
  permissions: [
    {
      permission: "connected_app:write",
      scopeId: ({ args }) => args.app.id,
    },
  ],
  fn: async ({
    tx,
    ctx,
    args: {
      app,
      name = app.name,
      identifier = app.identifier,
      urlPattern = app.urlPattern,
      configuration: baseConfiguration = app.configuration,
      configurationPatch,
      oauthConnectionId,
      note,
    },
  }) => {
    const configuration = applyConfigurationPatch(
      baseConfiguration,
      configurationPatch
    );
    if (oauthConnectionId !== undefined)
      throw userFacingError(
        "Connectors can’t reuse an existing OAuth credential"
      );
    if (
      identifier.trim() !== app.identifier &&
      [identifier.trim(), app.identifier].includes(
        MANAGED_AI_CONNECTOR_TYPE.identifier
      )
    ) {
      throw userFacingError("A 3B managed connector can’t change type");
    }
    if (
      app.authorizationMode === "visitor" &&
      !canAuthorizeVisitors({ ...app, identifier, configuration })
    ) {
      throw userFacingError(
        "Switch to owner authorization before changing how this connector authenticates"
      );
    }
    const identity = normalizedConnectorIdentity({
      name,
      identifier,
      urlPattern,
      configuration,
      previousConfiguration: app.configuration,
    });
    const registrationChanged = dcrRegistrationChanged(app, {
      ...identity,
      oauthRequestedScopes: app.oauthRequestedScopes,
    });
    const destinationChanged =
      identity.urlPattern !== app.urlPattern ||
      [
        ...new Set([
          ...Object.keys(identity.configuration),
          ...Object.keys(app.configuration),
        ]),
      ].some(
        (key) =>
          !CA_CERTIFICATE_FIELDS.includes(key) &&
          identity.configuration[key] !== app.configuration[key]
      );
    const trustChanged = CA_CERTIFICATE_FIELDS.some(
      (field) =>
        trimmedCaCertificate(identity.configuration[field]) !==
        trimmedCaCertificate(app.configuration[field])
    );
    const presentedMetadata =
      destinationChanged ||
      (trustChanged && app.metadata[presentedCertificateStatus] === "pending")
        ? withoutPresentedCertificate(app.metadata)
        : trustChanged
          ? withoutPresentedCertificateTrust(app.metadata)
          : undefined;
    await tx.mutate.connectedApps.update({
      id: app.id,
      ...identity,
      ...(presentedMetadata && { metadata: presentedMetadata }),
      ...(note !== undefined && { note }),
      ...(registrationChanged && {
        configuration: withoutDiscovery(identity.configuration),
        oauthClientId: "",
        hasOAuthClientSecret: false,
      }),
    });
    if (registrationChanged && app.oauthConnectionId) {
      await disconnectOAuthConnection(tx, app.oauthConnectionId);
    }
    if (ctx.recordAuditMetadata) {
      ctx.recordAuditMetadata({
        ...connectorDestinationAuditMetadata(app, identity),
        ...(await certificateAuditMetadata(
          app.configuration,
          identity.configuration
        )),
      });
    }
  },
});
